import { useEffect, useState } from "react";

type CacheEntry<T> = {
  data: T | null;
  promise: Promise<T> | null;
  error: string | null;
};

export const singletonCache: Record<string, CacheEntry<any>> = {};

export function useSingletonAsync<T>(key: string, loader: () => Promise<T>) {
  const cache = singletonCache[key] ?? {
    data: null,
    promise: null,
    error: null
  };

  if (!singletonCache[key]) singletonCache[key] = cache;

  const [data, setData] = useState<T | null>(cache.data);
  const [loading, setLoading] = useState<boolean>(!cache.data && !cache.error);
  const [error, setError] = useState<string | null>(cache.error);

  useEffect(() => {
    if (cache.data || cache.promise) {
      // Already loading or loaded
      if (cache.data) setData(cache.data);
      if (cache.error) setError(cache.error);
      return;
    }

    cache.promise = loader()
      .then((result) => {
        cache.data = result;
        setData(result);
      })
      .catch((err) => {
        const msg = err.message ?? "Unknown error";
        cache.error = msg;
        setError(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [key]);

  return { data, loading, error };
}
